import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useAuthStore } from "../stores/auth";
import { Spinner } from "../components/ui/Spinner";

/** Loads the visitor's session once (GET /auth/me) and reports whether it's settled. */
function useSession() {
  const status = useAuthStore((state) => state.status);
  const fetchMe = useAuthStore((state) => state.fetchMe);

  useEffect(() => {
    if (status === "idle") {
      void fetchMe();
    }
  }, [status, fetchMe]);

  return { status, settled: status !== "idle" && status !== "loading" };
}

function Loading() {
  return (
    <div className="loading-center" style={{ minHeight: "100vh" }}>
      <Spinner size="lg" />
      <p className="text-muted">Loading…</p>
    </div>
  );
}

/**
 * Pages anyone can use — the home page, template gallery and previews, and
 * the create-a-site wizard — with the session loaded so they know whether
 * the visitor is signed in. Creating the site itself asks them to sign in.
 */
export function SessionRoute() {
  const { settled } = useSession();
  return settled ? <Outlet /> : <Loading />;
}

/**
 * Pages that need an account (dashboard, editor, store). Signed-out
 * visitors go to the login page and come back here afterwards.
 */
export function ProtectedRoute() {
  const { status, settled } = useSession();
  const location = useLocation();

  if (!settled) return <Loading />;
  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <Outlet />;
}
